/**
 * API Service Layer for FaceShot-ChopShop
 *
 * Uses relative URLs in production so the frontend can communicate
 * with the Render backend at the same origin.
 */

import type { ServiceCosts } from "@/hooks/useUserCredits";
import type { Pack } from "@/components/pricing/PackCard";
import { rememberTelegramSession } from "@/lib/signin-stash";
import { readReferralCode } from "@/lib/referral";
import type { ReelCosts, ReferralInfo, ReferralTerms } from "@/features/reel/types";

export function resolveCurrency(): string {
  return "MXN"; // MXN is the only base currency
}

const isDevelopment = import.meta.env.DEV;

// Use VITE_BACKEND_URL if set (for cross-origin deployments)
// Otherwise use VITE_PUBLIC_URL, else relative URLs (same-origin or Vite proxy in dev)
const API_BASE_URL = (import.meta.env.VITE_BACKEND_URL || import.meta.env.VITE_PUBLIC_URL || '').replace(/\/$/, '');

let tokenUpdatePromise: Promise<void> | null = null;

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  requestStatus?: RequestStatus;
  /** The HTTP status, when the server answered at all. */
  status?: number;
  /** A refused call's whole answer (its code, reason, message and figures). */
  body?: Record<string, unknown>;
}

/**
 * A refused or failed call the caller has to tell apart: the HTTP status (0
 * when no answer came back) and the server's error code (`error` in its body).
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string) {
    super(code);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
  }
}

type RequestStatus = 'ok' | 'timeout' | 'aborted' | 'error';

type ApiRequestOptions = RequestInit & {
  timeoutMs?: number;
};

// Keys whose values never reach the console (and, from there, the centralized
// browser log stream): credentials, plus the fields that identify a person. An
// account object (e.g. the /api/auth/me response) carries its id as a bare `id`.
const SENSITIVE_KEY_PARTS = ['token', 'password', 'secret', 'email', 'phone'];
const IDENTITY_KEYS = new Set([
  'id', '_id', 'userid', 'user_id', 'accountid', 'account_id', 'distinct_id', 'distinctid',
  'telegram_id', 'telegramid', 'chat_id', 'chatid',
  'name', 'first_name', 'firstname', 'last_name', 'lastname', 'full_name', 'fullname',
  'username', 'telegram_username',
]);

function isSensitiveLogKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return IDENTITY_KEYS.has(lowerKey) || SENSITIVE_KEY_PARTS.some((part) => lowerKey.includes(part));
}

interface User {
  id: string;
  email: string;
  credits: number;
  createdAt: string;
  /** Made by the Telegram studio's Buy credits tap; its email is a placeholder until one is added. */
  studio_account?: boolean;
  /** The address is confirmed (the emailed link, or a Google sign-in). Gates nothing today. */
  email_verified?: boolean;
}

interface StudioSignInResponse {
  token: string;
  user: {
    id: string;
    email: string | null;
    credits: number;
    studio_account: boolean;
    /** The Telegram account this sign-in came from, when it has a username. */
    telegram_username?: string | null;
  };
}

interface MergeStudioResponse {
  token: string;
  user: { id: string; email: string; credits: number };
}

/**
 * POST /merge-studio's 202: the password checked, the join waiting for the
 * Telegram account to confirm it in the bot (confirm_url), and the secret the
 * page polls /merge-studio/status with.
 */
interface MergeStudioPending {
  status: 'confirm';
  confirm_url: string;
  poll: string;
  expires_in_seconds: number;
  telegram_username: string | null;
}

/** POST /merge-studio/status: still waiting, or the website account's session. */
type MergeStudioStatus =
  | { status: 'pending' }
  | ({ status: 'done'; moved?: { credits: number } } & MergeStudioResponse);

interface Job {
  id: string;
  userId?: string;
  type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | string;
  inputUrl?: string;
  outputUrl?: string;
  createdAt: string;
  completedAt?: string;
  progress?: number;
  error?: string;
  failureReason?: string;
  resultAvailable?: boolean;
  resultExpiresAt?: string;
  mediaAvailable?: boolean;
  archived?: boolean;
  unavailableReason?: string;
  estimatedCompletion?: string;
}

interface LoginPayload {
  email: string;
  password: string;
}

interface SignupPayload extends LoginPayload {
  name?: string;
  promo_code?: string;
  /** The page's language: the confirmation email goes out in it (en unless it starts with es). */
  lang?: string;
  /** A friend's referral code; read from lib/referral.ts when not given. */
  ref?: string;
}

type PromoStatus = 'applied' | 'invalid' | 'already_used' | 'none';

interface PromoResult {
  status: PromoStatus;
  message: string;
  credits?: number;
}

interface SignupResponse {
  token: string;
  user: User;
  promo?: PromoResult | null;
  /** A confirmation email actually went out (Firebase's, or the SMTP fallback). */
  verification_email_sent?: boolean;
  /** Credits a friend's referral link gave on top of the sign-up gift. */
  referral_credits?: number;
}

/** POST /api/auth/google: the site's own session for a Google (Firebase) sign-in. */
interface GoogleSignInResponse {
  token: string;
  user: User;
  /** The Google account made a new account here (and got the sign-up credits). */
  is_new_user: boolean;
  referral_credits?: number;
}

interface PricingPlan {
  id: string;
  code: string;
  name: string;
  monthlyPriceMxn: number;
  monthlyPriceUsd: number;
  includedSeconds: number;
  description: string;
  addOns?: Array<{
    code: string;
    label: string;
    priceCents: number;
    billingModel: string;
    description: string;
  }>;
}

interface SKU {
  id: string;
  code: string;
  name: string;
  price: number;
  currency: string;
  vectorId: string;
  vectorName: string;
  vectorCode: string;
  baseCredits: number;
  basePriceUsd: string;
  basePriceCents: number;
  defaultFlags: string[];
  description: string;
}

interface CreditPack {
  type: string;
  points: number;
  priceCents: number;
  currency: string;
  name: string;
  description: string;
}

interface PricingQuote {
  skuCode: string;
  skuName: string;
  quantity: number;
  appliedFlags: string[];
  customerPriceCents: number;
  customerPriceUsd: string;
  internalCostCents: number;
  internalCostUsd: string;
  marginPercent: string;
  totalSeconds: number;
  secondsFromPlan: number;
  overageSeconds: number;
  overageCostCents: number;
  overageCostUsd: string;
  remainingPlanSeconds: number;
}

interface Flag {
  id: string;
  code: string;
  label: string;
  priceMultiplier: number;
  priceAddFlatUsd: string;
  priceAddFlatCents: number;
  description: string;
}

interface AccountPlan {
  hasPlan: boolean;
  plan?: {
    id: string;
    code: string;
    name: string;
    monthlyPriceUsd: string;
    includedSeconds: number;
    overageRatePerSecondUsd: string;
    description: string;
  };
  subscription?: {
    startDate: string;
    endDate: string;
    autoRenew: boolean;
    status: string;
  };
  usage?: {
    periodStart: string;
    periodEnd: string;
    secondsUsed: number;
    remainingSeconds: number;
    usagePercent: number;
  };
}

interface Order {
  id: number;
  skuCode: string;
  skuName: string;
  skuDescription: string;
  quantity: number;
  appliedFlags: string[];
  customerPriceCents: number;
  customerPriceUsd: string;
  internalCostCents: number;
  marginPercent: number;
  totalSeconds: number;
  overageSeconds: number;
  status: string;
  createdAt: string;
}

interface CreateOrderPayload {
  sku_code: string;
  quantity: number;
  flags: string[];
}

// Enhanced API Types
interface SKUConfig {
  id: number;
  sku_code: string;
  config_version: number;
  active: number;
  created_at: string;
  updated_at: string;
  steps: SKUToolStep[];
  customer_options: CustomerOption[];
}

interface SKUToolStep {
  id: number;
  config_id: number;
  step_order: number;
  step_name: string;
  http_method: string;
  required: number;
  condition_expression: string | null;
  params_template: Record<string, unknown>;
  timeout_seconds: number;
  retry_enabled: number;
  retry_max_attempts: number;
  retry_backoff_ms: number;
  created_at: string;
}

interface CustomerOption {
  id: number;
  config_id: number;
  option_key: string;
  option_label: string;
  option_type: 'text' | 'number' | 'dropdown' | 'radio' | 'checkbox' | 'file' | 'textarea';
  option_values: { value: string; label: string }[] | null;
  default_value: string | null;
  required: number;
  validation_rules: {
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    allowedTypes?: string[];
    maxSize?: number;
  } | null;
  help_text: string | null;
  display_order: number;
  created_at: string;
}

interface AdvancedJob {
  job_id: number;
  order_id: number;
  sku_code: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  customer_inputs: Record<string, unknown>;
  total_steps: number;
  completed_steps: number;
  result_data: unknown;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

interface JobStep {
  id: number;
  job_id: number;
  step_order: number;
  step_name: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
  input_params: Record<string, unknown>;
  output_data: unknown;
  error_message: string | null;
  retry_count: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

interface CreditLedgerEntry {
  id: string;
  service: 'corrido' | 'story' | 'ads' | 'purchase' | 'adjustment';
  entry_type: 'charge' | 'refund' | 'grant';
  job_id: string | null;
  delta: number;
  balance_after: number;
  reason: string;
  created_at: string;
}

interface BackendPricingPlan {
  id: string | number;
  code: string;
  name: string;
  monthly_price_usd?: string | number;
  monthly_price_cents?: number;
  included_seconds?: number;
  description?: string;
  add_ons?: Array<{
    code?: string;
    label?: string;
    price_cents?: number;
    billing_model?: string;
    description?: string;
  }>;
}

interface BackendSKU {
  id: string | number;
  code: string;
  name: string;
  price?: number;
  currency?: string;
  vector_id?: string;
  vector_name?: string;
  vector_code?: string;
  base_credits: number;
  base_price_usd: string;
  base_price_cents: number;
  default_flags: string[];
  description: string;
}

interface BackendFlag {
  id: string | number;
  code: string;
  label: string;
  price_multiplier: number;
  price_add_flat_usd: string;
  price_add_flat_cents: number;
  description: string;
}

interface BackendPricingQuote {
  sku_code: string;
  sku_name: string;
  quantity: number;
  applied_flags: string[];
  customer_price_cents: number;
  customer_price_usd: string;
  internal_cost_cents: number;
  internal_cost_usd: string;
  margin_percent: string;
  total_seconds: number;
  seconds_from_plan?: number;
  overage_seconds?: number;
  overage_cost_cents?: number;
  overage_cost_usd?: string;
  remaining_plan_seconds?: number;
}

interface BackendAccountPlan {
  has_plan: boolean;
  plan?: {
    id: string | number;
    code: string;
    name: string;
    monthly_price_usd: string;
    included_seconds: number;
    overage_rate_per_second_usd: string;
    description?: string;
  };
  subscription?: {
    start_date: string;
    end_date: string;
    auto_renew: boolean;
    status: string;
  };
  usage?: {
    period_start: string;
    period_end: string;
    seconds_used: number;
    remaining_seconds: number;
    usage_percent: number;
  };
}

interface MediaUploadResponse {
  urls: string[];
}

interface UserVideoRecord {
  id?: string;
  _id?: string;
  job_id?: string;
  type?: string;
  status?: string;
  url?: string;
  result_url?: string;
  outputUrl?: string;
  source_url?: string;
  target_url?: string;
  error?: string;
  createdAt?: string;
  created_at?: string;
  completedAt?: string;
  completed_at?: string;
  media_type?: string;
  thumbnail?: string;
  thumbnail_url?: string;
  title?: string;
  name?: string;
  media_available?: boolean;
  unavailable_reason?: string;
  archived?: boolean;
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.token = localStorage.getItem('auth_token');
  }

  async setToken(token: string | null): Promise<void> {
    const update = async () => {
      this.token = token;
      if (token) {
        localStorage.setItem('auth_token', token);
      } else {
        localStorage.removeItem('auth_token');
      }
      // Same-tab listeners (a flow mounted before sign-in resolved, e.g. the
      // corrido reconnect) learn about it here; 'storage' only reaches OTHER tabs.
      if (typeof window !== 'undefined') window.dispatchEvent(new Event('faceshot:auth-changed'));
    };

    if (tokenUpdatePromise) {
      tokenUpdatePromise = tokenUpdatePromise.then(() => update());
    } else {
      tokenUpdatePromise = update();
    }

    await tokenUpdatePromise;
    tokenUpdatePromise = null;
  }

  getToken() {
    return this.token;
  }

  private resolveRequestTimeoutMs(endpoint: string, options: ApiRequestOptions): number {
    if (typeof options.timeoutMs === 'number' && Number.isFinite(options.timeoutMs) && options.timeoutMs > 0) {
      return options.timeoutMs;
    }
    if (endpoint.startsWith('/media-agent/office/media') || endpoint.startsWith('/api/web/creations')) {
      return 45000;
    }
    return 15000;
  }

  private async request<T>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const timeoutMs = this.resolveRequestTimeoutMs(endpoint, options);
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    try {
      const url = `${this.baseUrl}${endpoint}`;
      const currency = resolveCurrency();

      const headers: HeadersInit = {
        'x-currency': currency,
        ...options.headers,
      };

      if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }

      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }

      // Log request details (redacting sensitive info)
      console.debug('[API Request]', {
        endpoint,
        method: options.method || 'GET',
        headers: {
          ...headers,
          Authorization: headers['Authorization'] ? 'Bearer ***' : undefined
        },
        body: options.body instanceof FormData
          ? '[FormData]'
          : this.redactSensitiveFields(options.body)
      });

      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      // Log successful response
      console.debug('[API Response]', {
        endpoint,
        status: response.status,
        data: this.redactSensitiveFields(data)
      });

      if (!response.ok) {
        console.error('[API Error]', {
          endpoint,
          status: response.status,
          error: data.error || data.message || 'Request failed',
          responseData: this.redactSensitiveFields(data)
        });

        return {
          success: false,
          error: data.error || data.message || 'Request failed',
          requestStatus: 'error',
          status: response.status,
          body: data && typeof data === 'object' ? data : undefined,
        };
      }

      return { success: true, data, requestStatus: 'ok', status: response.status };
    } catch (error) {
      clearTimeout(timeoutId);
      const message = error instanceof Error ? error.message : 'Network error';
      const isAbortError = (error instanceof DOMException && error.name === 'AbortError') || /aborted/i.test(message);
      if (isAbortError) {
        const requestStatus: RequestStatus = timedOut ? 'timeout' : 'aborted';
        console.debug('[API Controlled Abort]', {
          endpoint,
          requestStatus,
          error: message,
        });
        return {
          success: false,
          error: timedOut ? 'Request timed out' : 'Request aborted',
          requestStatus,
        };
      }
      console.error('[API Error]', {
        endpoint,
        error: message,
        isTimeout: false
      });

      return {
        success: false,
        error: message,
        requestStatus: 'error',
      };
    }
  }

  private redactSensitiveFields(data: unknown): unknown {
    // Request bodies reach this method as a serialized JSON string
    // (JSON.stringify(payload)). Parse the string first so the redaction below
    // runs on its fields, then re-serialize. Without this the whole body,
    // including the plaintext password and email, was logged untouched. A
    // string that is not a JSON object is returned unchanged.
    if (typeof data === 'string') {
      let parsed: unknown;
      try {
        parsed = JSON.parse(data);
      } catch {
        return data;
      }
      if (typeof parsed !== 'object' || parsed === null) return data;
      return JSON.stringify(this.redactSensitiveFields(parsed));
    }

    if (typeof data !== 'object' || data === null) return data;

    if (data instanceof FormData) return '[FormData]';

    // Create a copy to avoid modifying the original
    const result = Array.isArray(data) ? [...data] : { ...data };

    // Redact sensitive fields
    for (const key in result) {
      if (isSensitiveLogKey(key)) {
        result[key] = '***';
      } else if (typeof result[key] === 'object') {
        result[key] = this.redactSensitiveFields(result[key]);
      }
    }

    return result;
  }

  private resolveCheckoutUrl(payload: unknown): string | null {
    if (!payload || typeof payload !== 'object') return null;
    const record = payload as Record<string, unknown>;
    const direct = [record.url, record.checkout_url, record.session_url]
      .find((value) => typeof value === 'string' && value.trim().length > 0);
    if (typeof direct === 'string') return direct;
    const nestedData = record.data;
    if (!nestedData || typeof nestedData !== 'object') return null;
    const nestedRecord = nestedData as Record<string, unknown>;
    const nested = [nestedRecord.url, nestedRecord.checkout_url, nestedRecord.session_url]
      .find((value) => typeof value === 'string' && value.trim().length > 0);
    return typeof nested === 'string' ? nested : null;
  }

  // Auth endpoints
  async login(payload: LoginPayload): Promise<ApiResponse<{ token: string; user: User }>> {
    console.log('[AuthApi] Login start');
    const result = await this.request<{ token: string; user: User }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (result.success && result.data?.token) {
      await this.setToken(result.data.token);
      console.log('[AuthApi] Login success, token stored');
    } else {
      console.warn('[AuthApi] Login failed', { error: result.error });
    }

    return result;
  }

  async signup(payload: SignupPayload): Promise<ApiResponse<SignupResponse>> {
    console.log('[AuthApi] Signup start');
    // A friend's ?ref= link seen in the last 30 days (lib/referral.ts).
    const ref = payload.ref ?? readReferralCode() ?? undefined;
    const result = await this.request<SignupResponse>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify(ref ? { ...payload, ref } : payload),
    });

    if (result.success && result.data?.token) {
      await this.setToken(result.data.token);
      console.log('[AuthApi] Signup success, token stored');
    } else {
      console.warn('[AuthApi] Signup failed', { error: result.error });
    }

    return result;
  }

  /**
   * A Firebase ID token from "Continue with Google" (lib/google-signin.ts),
   * exchanged for the website's normal session and stored exactly as a
   * password login stores it. Not a Telegram session: no
   * rememberTelegramSession. The field is id_token so the request log's
   * redaction (any key containing "token") hides it.
   */
  async googleSignIn(idToken: string, lang?: string): Promise<ApiResponse<GoogleSignInResponse>> {
    console.log('[AuthApi] Google sign-in start');
    const result = await this.request<GoogleSignInResponse>('/api/auth/google', {
      method: 'POST',
      body: JSON.stringify({ id_token: idToken, lang, ref: readReferralCode() ?? undefined }),
    });

    if (result.success && result.data?.token) {
      await this.setToken(result.data.token);
      console.log('[AuthApi] Google sign-in success, token stored');
    } else {
      console.warn('[AuthApi] Google sign-in failed', { error: result.error });
    }

    return result;
  }

  /**
   * The one-time sign-in token from the Telegram studio's "Buy credits" tap
   * (services/studio-signin.js), exchanged for the website's normal session.
   * On success the session is stored exactly as a password login stores it,
   * so every page that reads the token works unchanged.
   */
  async studioSignIn(token: string): Promise<ApiResponse<StudioSignInResponse>> {
    const result = await this.request<StudioSignInResponse>('/api/web/user/studio-signin', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    if (result.success && result.data?.token) {
      await this.setToken(result.data.token);
      rememberTelegramSession(result.data.token);
    }
    return result;
  }

  /**
   * The Python bot's top-up token (telegram-bot/billing.py, the landing page's
   * /?from=bot&token=...), exchanged for the website's normal session. The
   * same answer and the same storage as studioSignIn.
   */
  async botSignIn(token: string): Promise<ApiResponse<StudioSignInResponse>> {
    const result = await this.request<StudioSignInResponse>('/api/web/user/bot-signin', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    if (result.success && result.data?.token) {
      await this.setToken(result.data.token);
      rememberTelegramSession(result.data.token);
    }
    return result;
  }

  /** A studio account (made from a Telegram identity) gets a real email and password. */
  async addEmail(email: string, password: string): Promise<ApiResponse<{ ok: boolean; email: string }>> {
    return this.request<{ ok: boolean; email: string }>('/api/web/user/add-email', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  /**
   * Signed in as a studio account, sign in to the website account that
   * already exists: the studio account folds into it and its Telegram id moves
   * there. The server checks the password and answers 202 with a bot link:
   * nothing is joined until that Telegram account confirms it there
   * (mergeStudioStatus). An answer with a session (an older server) replaces
   * the stored one at once.
   */
  async mergeStudio({ email, password }: { email: string; password: string }): Promise<ApiResponse<MergeStudioResponse | MergeStudioPending>> {
    const result = await this.request<MergeStudioResponse | MergeStudioPending>('/api/web/user/merge-studio', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (result.success && result.data && 'token' in result.data && result.data.token) {
      await this.setToken(result.data.token);
      rememberTelegramSession(result.data.token);
    }
    return result;
  }

  /**
   * The join mergeStudio started, once Telegram confirmed it: 'done' carries
   * the website account's session, which replaces the stored one. A refusal
   * is a 409 (its code and reason in `body`), an expired one a 410.
   */
  async mergeStudioStatus(poll: string): Promise<ApiResponse<MergeStudioStatus>> {
    const result = await this.request<MergeStudioStatus>('/api/web/user/merge-studio/status', {
      method: 'POST',
      body: JSON.stringify({ poll }),
    });
    if (result.success && result.data && result.data.status === 'done' && result.data.token) {
      await this.setToken(result.data.token);
      rememberTelegramSession(result.data.token);
    }
    return result;
  }

  async logout(): Promise<void> {
    this.setToken(null);
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    return this.request<User>('/api/auth/me');
  }

  // Job/Processing endpoints
  // processJob (POST /api/web/process) is gone: that route only reached the
  // retired A2E service.
  async getJobStatus(jobId: string): Promise<ApiResponse<Job>> {
    return this.request<Job>(`/api/web/status?id=${jobId}`);
  }

  async getJobHistory(includeOlder = false): Promise<ApiResponse<Job[]>> {
    const query = includeOlder ? '?include_older=true' : '';
    return this.request<Job[]>(`/api/web/creations${query}`);
  }

  // Credits & Payments — credits are consumed ONLY by the two local ComfyUI
  // services (corrido, story, ads); service_costs carries the per-run schedule
  // (ads is a per-offer ladder: spot_15 … package_60).
  /** What each studio on the Reel costs, from the live config (routes/reel.js). Public. */
  async getReelCosts(): Promise<ApiResponse<{ costs: ReelCosts }>> {
    return this.request<{ costs: ReelCosts }>('/api/web/reel/costs');
  }

  /** Give-get referral terms: is it on, and how many credits each side gets. Public. */
  async getReferralTerms(): Promise<ApiResponse<ReferralTerms>> {
    return this.request<ReferralTerms>('/api/web/referral/terms');
  }

  /** This account's referral code and how many friends joined and bought. Signed in. */
  async getReferral(): Promise<ApiResponse<ReferralInfo>> {
    return this.request<ReferralInfo>('/api/web/referral');
  }

  async getCredits(): Promise<ApiResponse<{ balance: number; service_costs?: ServiceCosts }>> {
    return this.request<{ balance: number; service_costs?: ServiceCosts }>('/api/web/credits');
  }

  async getPurchases(): Promise<ApiResponse<{
    purchases: Array<{
      id: string;
      amount_cents: number;
      credits_purchased: number;
      pack_type?: string;
      currency?: string;
      created_at: string;
      status?: string;
    }>;
    count: number;
  }>> {
    return this.request('/api/web/purchases');
  }

  async getPricingPlans(): Promise<ApiResponse<PricingPlan[]>> {

    const result = await this.request<{ plans: BackendPricingPlan[] }>('/api/plans');

    if (!result.success || !result.data) {
      return {
        success: false,
        error: result.error || 'Failed to load pricing plans',
      };
    }

    const plans = Array.isArray(result.data.plans) ? result.data.plans : [];

    const normalized: PricingPlan[] = plans.map((plan: BackendPricingPlan) => ({
      id: String(plan.id),
      code: String(plan.code),
      name: String(plan.name),
      monthlyPriceMxn: plan.monthly_price_cents
        ? Number(plan.monthly_price_cents) / 100
        : plan.monthly_price_usd
          ? parseFloat(String(plan.monthly_price_usd))
          : 0,
      monthlyPriceUsd: plan.monthly_price_usd
        ? parseFloat(String(plan.monthly_price_usd))
        : plan.monthly_price_cents
          ? Number(plan.monthly_price_cents) / 100
          : 0,
      includedSeconds: Number(plan.included_seconds) || 0,
      description: String(plan.description || ''),
      addOns: Array.isArray(plan.add_ons)
        ? plan.add_ons.map((item) => ({
          code: String(item.code || ''),
          label: String(item.label || ''),
          priceCents: Number(item.price_cents) || 0,
          billingModel: String(item.billing_model || ''),
          description: String(item.description || ''),
        })).filter((item) => item.code && item.label)
        : [],
    }));

    return {
      success: true,
      data: normalized,
    };
  }

  async createCheckoutSession(planId: string): Promise<ApiResponse<{ url: string }>> {
    const result = await this.request<{ session_url?: string; session_id?: string; url?: string; checkout_url?: string; data?: { session_url?: string; url?: string; checkout_url?: string } }>('/api/subscribe', {
      method: 'POST',
      body: JSON.stringify({ plan_id: planId }),
    });

    if (!result.success || !result.data) {
      return {
        success: false,
        error: result.error || 'Unable to start subscription checkout',
      };
    }

    const checkoutUrl = this.resolveCheckoutUrl(result.data);
    if (!checkoutUrl) {
      return {
        success: false,
        error: 'Unable to start subscription checkout',
      };
    }

    return {
      success: true,
      data: { url: checkoutUrl },
    };
  }

  async createBundleCheckout(skuCode: string): Promise<ApiResponse<{ url: string }>> {
    const result = await this.request<{ session_url?: string; session_id?: string; url?: string; checkout_url?: string; data?: { session_url?: string; url?: string; checkout_url?: string } }>('/api/web/bundle-checkout', {
      method: 'POST',
      body: JSON.stringify({ sku_code: skuCode }),
    });

    if (!result.success || !result.data) {
      return {
        success: false,
        error: result.error || 'Unable to start bundle checkout',
      };
    }

    const checkoutUrl = this.resolveCheckoutUrl(result.data);
    if (!checkoutUrl) {
      return {
        success: false,
        error: 'Unable to start bundle checkout',
      };
    }

    return {
      success: true,
      data: { url: checkoutUrl },
    };
  }

  async createServiceCheckout(
    skuCode: string,
    quantity = 1,
    flags: string[] = []
  ): Promise<ApiResponse<{ url: string }>> {
    const result = await this.request<{ session_url?: string; session_id?: string; url?: string }>('/api/web/service-checkout', {
      method: 'POST',
      body: JSON.stringify({ sku_code: skuCode, quantity, flags }),
    });

    if (!result.success || !result.data) {
      return {
        success: false,
        error: result.error || 'Unable to start service checkout',
      };
    }

    const checkoutUrl = this.resolveCheckoutUrl(result.data);
    if (!checkoutUrl) {
      return {
        success: false,
        error: 'Unable to start service checkout',
      };
    }

    return {
      success: true,
      data: { url: checkoutUrl },
    };
  }

  async createCreditPackCheckout(packType: string): Promise<ApiResponse<{ url: string }>> {
    const result = await this.request<{ url?: string; session_url?: string; checkout_url?: string }>('/api/web/checkout', {
      method: 'POST',
      body: JSON.stringify({ pack_type: packType }),
    });

    if (!result.success || !result.data) {
      return {
        success: false,
        error: result.error || 'Unable to start credit pack checkout',
      };
    }

    const checkoutUrl = this.resolveCheckoutUrl(result.data);
    if (!checkoutUrl) {
      return {
        success: false,
        error: 'Unable to start credit pack checkout',
      };
    }

    return {
      success: true,
      data: { url: checkoutUrl },
    };
  }

  /** Every pack in shared/config/packs.js (per-video and credits), as /api/web/packs serves them. */
  async getPacks(): Promise<Pack[]> {
    const result = await this.request<{ data?: Pack[] }>('/api/web/packs');
    if (!result.success || !result.data) {
      throw new ApiRequestError(result.status ?? 0, result.error || 'packs_unavailable');
    }
    return Array.isArray(result.data.data) ? result.data.data : [];
  }

  /**
   * Stripe Checkout for one pack. `return_to` 'studio' (a visit from the
   * Telegram studio) or 'bot' (from the bot) sends the buyer back to
   * /billing/success; the server takes it as an enum, never an address.
   * `back` (a studio's pay sheet) returns to that studio's console instead.
   * Throws ApiRequestError.
   */
  async createCheckout({ pack_type, return_to, back, run }: { pack_type: string; return_to?: 'studio' | 'bot'; back?: 'music' | 'ads' | 'tiktok'; run?: string }): Promise<{ url: string }> {
    const result = await this.request<{ url?: string; session_url?: string; checkout_url?: string }>('/api/web/checkout', {
      method: 'POST',
      body: JSON.stringify({ pack_type, ...(return_to ? { return_to } : {}), ...(back ? { back } : {}), ...(back && run ? { run } : {}) }),
    });
    if (!result.success || !result.data) {
      throw new ApiRequestError(result.status ?? 0, result.error || 'checkout_failed');
    }
    const url = this.resolveCheckoutUrl(result.data);
    if (!url) throw new ApiRequestError(result.status ?? 0, 'checkout_failed');
    return { url };
  }

  async getCreditPacks(): Promise<ApiResponse<CreditPack[]>> {
    const result = await this.request<{ data?: Array<{ type?: string; points?: number; price_cents?: number; currency?: string }> }>('/api/web/packs');
    if (!result.success || !result.data) {
      return {
        success: false,
        error: result.error || 'Failed to load credit packs',
      };
    }

    const labelByType: Record<string, string> = {
      credits_10: 'Advanced Credits',
      credits_50: 'Advanced Credits',
      micro: 'Launch Pack',
      starter: 'Advanced Bundle',
      plus: 'Advanced Bundle',
      pro: 'Advanced Bundle',
    };
    const descriptionByType: Record<string, string> = {
      credits_10: 'Advanced pay-as-you-go credits for internal tool usage.',
      credits_50: 'Advanced pay-as-you-go credits for internal tool usage.',
      micro: 'A one-time first purchase for export-ready launch output.',
      starter: 'Advanced pay-as-you-go bundle for repeat workspace usage.',
      plus: 'Advanced pay-as-you-go bundle for higher-volume workspace usage.',
      pro: 'Advanced pay-as-you-go bundle for power users and teams.',
    };
    const allowedBundleTypes = new Set(Object.keys(labelByType));

    const packs = (result.data.data || [])
      .map((pack) => {
        const type = typeof pack?.type === 'string' ? pack.type.trim() : '';
        const points = Number(pack?.points) || 0;
        const priceCents = Number(pack?.price_cents) || 0;
        if (!type || !allowedBundleTypes.has(type) || points <= 0 || priceCents < 99) return null;
        return {
          type,
          points,
          priceCents,
          currency: 'mxn',
          name: labelByType[type] || type.replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()),
          description: descriptionByType[type] || `${points.toLocaleString()} credits package.`,
        } satisfies CreditPack;
      })
      .filter((pack): pack is CreditPack => Boolean(pack))
      .sort((a, b) => a.priceCents - b.priceCents);

    return {
      success: true,
      data: packs,
    };
  }

  // SKUs
  async getSKUs(vectorId?: string): Promise<ApiResponse<SKU[]>> {
    const params = vectorId ? `?vector_id=${vectorId}` : '';
    const result = await this.request<{ skus: BackendSKU[] }>(`/api/skus${params}`);

    if (!result.success || !result.data) {
      return {
        success: false,
        error: result.error || 'Failed to load SKUs',
      };
    }

    const skus: SKU[] = (result.data.skus || [])
      .filter((sku: BackendSKU) => sku && sku.id && sku.code && sku.name)
      .map((sku: BackendSKU) => ({
        id: String(sku.id),
        code: String(sku.code),
        name: String(sku.name),
        price: sku.price ?? 0,
        currency: (sku.currency || resolveCurrency()).toUpperCase(),
        vectorId: String(sku.vector_id || ''),
        vectorName: String(sku.vector_name || ''),
        vectorCode: String(sku.vector_code || ''),
        baseCredits: Number(sku.base_credits) || 0,
        basePriceUsd: String(sku.base_price_usd || '0.00'),
        basePriceCents: Number(sku.base_price_cents) || 0,
        defaultFlags: Array.isArray(sku.default_flags) ? sku.default_flags : [],
        description: String(sku.description || ''),
      }));

    return {
      success: true,
      data: skus,
    };
  }

  // Flags
  async getFlags(): Promise<ApiResponse<Flag[]>> {
    const result = await this.request<{ flags: BackendFlag[] }>('/api/flags');

    if (!result.success || !result.data) {
      return {
        success: false,
        error: result.error || 'Failed to load flags',
      };
    }

    const flags: Flag[] = (result.data.flags || []).map((flag: BackendFlag) => ({
      id: String(flag.id),
      code: String(flag.code),
      label: String(flag.label),
      priceMultiplier: Number(flag.price_multiplier) || 1.0,
      priceAddFlatUsd: String(flag.price_add_flat_usd || '0.00'),
      priceAddFlatCents: Number(flag.price_add_flat_cents) || 0,
      description: String(flag.description || ''),
    }));

    return {
      success: true,
      data: flags,
    };
  }

  // Pricing Quote
  async getPricingQuote(
    skuCode: string,
    quantity: number = 1,
    flags: string[] = []
  ): Promise<ApiResponse<PricingQuote>> {
    const result = await this.request<{ quote: BackendPricingQuote }>('/api/pricing/quote', {
      method: 'POST',
      body: JSON.stringify({ sku_code: skuCode, quantity, flags, currency: resolveCurrency() }),
    });

    if (!result.success || !result.data?.quote) {
      return {
        success: false,
        error: result.error || 'Failed to get pricing quote',
      };
    }

    const q: BackendPricingQuote = result.data.quote;
    const quote: PricingQuote = {
      skuCode: String(q.sku_code),
      skuName: String(q.sku_name),
      quantity: Number(q.quantity),
      appliedFlags: Array.isArray(q.applied_flags) ? q.applied_flags : [],
      customerPriceCents: Number(q.customer_price_cents),
      customerPriceUsd: String(q.customer_price_usd),
      internalCostCents: Number(q.internal_cost_cents),
      internalCostUsd: String(q.internal_cost_usd),
      marginPercent: String(q.margin_percent),
      totalSeconds: Number(q.total_seconds),
      secondsFromPlan: Number(q.seconds_from_plan || 0),
      overageSeconds: Number(q.overage_seconds || 0),
      overageCostCents: Number(q.overage_cost_cents || 0),
      overageCostUsd: String(q.overage_cost_usd || '0.00'),
      remainingPlanSeconds: Number(q.remaining_plan_seconds || 0),
    };

    return {
      success: true,
      data: quote,
    };
  }

  // Account Plan Info
  async getAccountPlan(): Promise<ApiResponse<AccountPlan>> {
    const result = await this.request<BackendAccountPlan>('/api/account/plan');

    if (!result.success || !result.data) {
      return {
        success: false,
        error: result.error || 'Failed to load account plan',
      };
    }

    const data: BackendAccountPlan = result.data;

    if (!data.has_plan) {
      return {
        success: true,
        data: { hasPlan: false },
      };
    }

    const accountPlan: AccountPlan = {
      hasPlan: true,
      plan: data.plan ? {
        id: String(data.plan.id),
        code: String(data.plan.code),
        name: String(data.plan.name),
        monthlyPriceUsd: String(data.plan.monthly_price_usd),
        includedSeconds: Number(data.plan.included_seconds),
        overageRatePerSecondUsd: String(data.plan.overage_rate_per_second_usd),
        description: String(data.plan.description || ''),
      } : undefined,
      subscription: data.subscription ? {
        startDate: String(data.subscription.start_date),
        endDate: String(data.subscription.end_date),
        autoRenew: Boolean(data.subscription.auto_renew),
        status: String(data.subscription.status),
      } : undefined,
      usage: data.usage ? {
        periodStart: String(data.usage.period_start),
        periodEnd: String(data.usage.period_end),
        secondsUsed: Number(data.usage.seconds_used),
        remainingSeconds: Number(data.usage.remaining_seconds),
        usagePercent: Number(data.usage.usage_percent),
      } : undefined,
    };

    return {
      success: true,
      data: accountPlan,
    };
  }

  // Orders
  async createOrder(payload: CreateOrderPayload): Promise<ApiResponse<{ order_id: number; quote: PricingQuote; status: string; message: string }>> {
    return this.request<{ order_id: number; quote: PricingQuote; status: string; message: string }>('/api/orders/create', {
      method: 'POST',
      body: JSON.stringify({ ...payload, currency: resolveCurrency() }),
    });
  }

  async getOrders(limit: number = 20, offset: number = 0): Promise<ApiResponse<{ orders: Order[] }>> {
    return this.request<{ orders: Order[] }>(`/api/orders?limit=${limit}&offset=${offset}`);
  }

  async getOrder(orderId: number): Promise<ApiResponse<{ order: Order }>> {
    return this.request<{ order: Order }>(`/api/orders/${orderId}`);
  }

  // Stats (public)
  async getStats(): Promise<ApiResponse<{ videos: number; paying_users: number; total_users: number }>> {
    return this.request<{ videos: number; paying_users: number; total_users: number }>('/stats');
  }

  // Enhanced API - SKU Tool Configurations
  async getSKUConfig(skuCode: string): Promise<ApiResponse<SKUConfig>> {
    return this.request<SKUConfig>(`/api/skus/${skuCode}/config`);
  }

  async validateCustomerInputs(skuCode: string, inputs: Record<string, unknown>): Promise<ApiResponse<{ valid: boolean; errors: string[] }>> {
    return this.request<{ valid: boolean; errors: string[] }>(`/api/skus/${skuCode}/validate`, {
      method: 'POST',
      body: JSON.stringify({ inputs }),
    });
  }

  async createAdvancedJob(skuCode: string, customerInputs: Record<string, unknown>): Promise<ApiResponse<AdvancedJob>> {
    return this.request<AdvancedJob>('/api/jobs/create-advanced', {
      method: 'POST',
      body: JSON.stringify({ sku_code: skuCode, customer_inputs: customerInputs }),
    });
  }

  async getAdvancedJobStatus(jobId: number): Promise<ApiResponse<AdvancedJob>> {
    return this.request<AdvancedJob>(`/api/jobs/${jobId}/status`);
  }

  async getJobSteps(jobId: number): Promise<ApiResponse<{ steps: JobStep[] }>> {
    return this.request<{ steps: JobStep[] }>(`/api/jobs/${jobId}/steps`);
  }

  // Enhanced API - Media Upload
  async uploadMedia(formData: FormData): Promise<ApiResponse<MediaUploadResponse>> {
    // Check if we're uploading a single file or multiple files
    // The frontend sends formData with 'file' or 'files' field
    const hasMultipleFiles = formData.has('files');

    if (hasMultipleFiles) {
      // For multiple files, use /api/upload/multiple
      const result = await this.request<{ files: Array<{ url: string }> }>('/api/upload/multiple', {
        method: 'POST',
        body: formData
      });

      if (result.success && result.data?.files) {
        return {
          success: true,
          data: {
            urls: result.data.files.map(file => file.url)
          }
        };
      }

      return {
        success: false,
        error: result.error || 'Failed to upload files'
      } as unknown as ApiResponse<MediaUploadResponse>;
    } else {
      // For single file, use /api/upload/media
      const result = await this.request<{ url: string }>('/api/upload/media', {
        method: 'POST',
        body: formData
      });

      if (result.success && result.data?.url) {
        return {
          success: true,
          data: {
            urls: [result.data.url]
          }
        };
      }

      return {
        success: false,
        error: result.error || 'Failed to upload file'
      } as unknown as ApiResponse<MediaUploadResponse>;
    }
  }

  // A2E is fully deprecated — credits come only from the ComfyUI services
  // (corrido music videos + TikTok stories) via /api/web/credits and its ledger.
  async getCreditLedger(limit = 50): Promise<ApiResponse<{ entries: CreditLedgerEntry[] }>> {
    return this.request<{ entries: CreditLedgerEntry[] }>(`/api/web/credits/ledger?limit=${limit}`);
  }

  async getUserVideoRecords(includeArchived = false): Promise<ApiResponse<{ rows: UserVideoRecord[] }>> {
    const query = includeArchived ? '?include_archived=true' : '';
    return this.request<{ rows: UserVideoRecord[] }>(`/media-agent/office/media${query}`, { method: 'GET' });
  }

  async createUserMediaRecord(payload: { url: string; type: string; media_type?: string; title?: string }): Promise<ApiResponse<{ ok: boolean }>> {
    return this.request<{ ok: boolean }>('/media-agent/office/media', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async deleteUserMediaRecord(target: { id?: string; url?: string; job_id?: string }): Promise<ApiResponse<{ ok: boolean }>> {
    return this.request<{ ok: boolean }>('/media-agent/office/media', {
      method: 'DELETE',
      body: JSON.stringify(target)
    });
  }
}

export const api = new ApiClient(API_BASE_URL);

// Media upload utilities
export const uploadedFiles: Record<string, File> = {};

export function handleFileSelect(input: HTMLInputElement, fieldId: string) {
  const file = input.files?.[0];
  if (file) {
    uploadedFiles[fieldId] = file;
  }
}

export function handleDrop(event: React.DragEvent, fieldId: string) {
  event.preventDefault();
  const file = event.dataTransfer.files?.[0];
  if (file) {
    uploadedFiles[fieldId] = file;
  }
}

async function uploadAllFiles(apiBase: string) {
  const uploadedUrls: Record<string, string> = {};

  for (const [fieldId, file] of Object.entries(uploadedFiles)) {
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(`${apiBase}/upload`, {
      method: 'POST',
      body: formData
    });

    const data = await res.json();
    uploadedUrls[fieldId] = data.url;
  }

  return uploadedUrls;
}

async function startJob(
  apiBase: string,
  userId: string,
  service: string,
  uploadedUrls: Record<string, string>,
  textInputs: Record<string, string>
) {
  const res = await fetch(`${apiBase}/process`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userId,
      service,
      files: uploadedUrls,
      inputs: textInputs,
    }),
  });

  const data = await res.json();
  return data.taskId || data.job_id || (data.job && data.job.id);
}

async function pollForResult(
  apiBase: string,
  taskId: string,
  service: string
) {
  const maxAttempts = 60;
  let attempts = 0;

  while (attempts < maxAttempts) {
    await new Promise(r => setTimeout(r, 3000));

    const res = await fetch(`${apiBase}/status?taskId=${taskId}&service=${service}`);
    const data = await res.json();

    if (data.status === 'completed' && data.result_url) {
      return data.result_url;
    }
    if (data.status === 'failed') {
      throw new Error('Processing failed');
    }

    attempts++;
  }

  throw new Error('Processing timed out');
}

export type {
  User,
  Job,
  Pack,
  StudioSignInResponse,
  MergeStudioResponse,
  MergeStudioPending,
  MergeStudioStatus,
  LoginPayload,
  SignupPayload,
  SignupResponse,
  GoogleSignInResponse,
  PromoResult,
  PricingPlan,
  SKU,
  CreditPack,
  PricingQuote,
  Flag,
  AccountPlan,
  Order,
  CreateOrderPayload,
  ApiResponse,
  // Enhanced API Types
  SKUConfig,
  SKUToolStep,
  CustomerOption,
  AdvancedJob,
  JobStep,
  CreditLedgerEntry,
  MediaUploadResponse,
  UserVideoRecord
};
